import React from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'motion/react';
import { ConfirmationOverlay } from './SharedStyles';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, BORDER_RADIUS, SPACING } from './constants';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal = styled(motion.div)`
  background: ${COLORS.background};
  border-radius: ${BORDER_RADIUS.md};
  padding: ${SPACING.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 32px ${COLORS.shadow};
`;

const ConfirmationTitle = styled.h3`
  font-size: ${FONT_SIZES.lg};
  font-weight: ${FONT_WEIGHTS.semibold};
  color: ${COLORS.text};
  margin: 0 0 ${SPACING.md} 0;
`;

const ConfirmationMessage = styled.p`
  font-size: ${FONT_SIZES.sm};
  color: ${COLORS.textSecondary};
  margin: 0 0 ${SPACING.xl} 0;
  line-height: 1.4;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: ${SPACING.md};
  justify-content: flex-end;
`;

const ConfirmationButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${SPACING.sm} ${SPACING.lg};
  border-radius: ${BORDER_RADIUS.sm};
  font-size: ${FONT_SIZES.sm};
  font-weight: ${FONT_WEIGHTS.medium};
  cursor: pointer;
  border: none;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: ${COLORS.primary};
    color: ${COLORS.white};
  `
      : `
    background: #f5f5f5;
    color: ${COLORS.text};
  `}

  &:hover {
    opacity: 0.9;
  }
`;

export const ConfirmationDialog = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <ConfirmationOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onCancel}>
          <ConfirmationModal
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ConfirmationTitle>{title}</ConfirmationTitle>
            <ConfirmationMessage>{message}</ConfirmationMessage>
            <ConfirmationButtons>
              <ConfirmationButton variant="secondary" onClick={onCancel}>
                {cancelText}
              </ConfirmationButton>
              <ConfirmationButton variant="primary" onClick={onConfirm}>
                {confirmText}
              </ConfirmationButton>
            </ConfirmationButtons>
          </ConfirmationModal>
        </ConfirmationOverlay>
      )}
    </AnimatePresence>
  );
};
